import { Button, Form, Input, Modal, Select, Space, Upload } from 'antd';
import { CloudUploadOutlined, DeleteOutlined } from '@ant-design/icons';

const XhsNoteModal = ({
  open,
  form,
  confirmLoading,
  imageFileList,
  summaryLoading,
  tagsLoading,
  creating,
  onOk,
  onCancel,
  onGenerateSummary,
  onGenerateTags,
  onImagesChange,
  onRemoveImage,
  getPreviewUrl,
  readDragUid,
  handleDropOn,
  handleDropToEnd,
  dragUid,
  dropHint,
  setDragUid,
  setDropHint,
}) => (
  <Modal
    title="新建小红书笔记"
    open={open}
    onOk={onOk}
    confirmLoading={confirmLoading}
    okText="创建"
    cancelText="取消"
    width={900}
    onCancel={onCancel}
  >
    <Form form={form} layout="vertical" initialValues={{ tags: [] }}>
      <Form.Item
        label="标题"
        name="title"
        rules={[{ required: true, message: '请输入标题' }]}
      >
        <Input placeholder="请输入标题" maxLength={20} showCount />
      </Form.Item>

      <Form.Item
        label={(
          <Space size="small">
            <span>内容简介</span>
            <Button
              type="link"
              size="small"
              loading={summaryLoading}
              onClick={(e) => {
                e.preventDefault();
                onGenerateSummary();
              }}
            >
              AI生成
            </Button>
          </Space>
        )}
        name="summary"
        extra="可手动填写；也可点击 AI生成（基于正文）。"
      >
        <Input.TextArea rows={3} placeholder="用于列表展示的简介（可选）" />
      </Form.Item>

      <Form.Item
        label="正文"
        name="body"
        rules={[{ required: true, message: '请输入正文' }]}
      >
        <Input.TextArea rows={8} placeholder="请输入正文" />
      </Form.Item>

      <Form.Item
        label={(
          <Space size="small">
            <span>标签</span>
            <Button
              type="link"
              size="small"
              loading={tagsLoading}
              onClick={(e) => {
                e.preventDefault();
                onGenerateTags();
              }}
            >
              AI生成
            </Button>
          </Space>
        )}
        name="tags"
      >
        <Select mode="tags" placeholder="回车添加标签" style={{ width: '100%' }} />
      </Form.Item>

      <Form.Item
        label="图片（拖动可调整顺序）"
        required
        extra="最多 18 张，支持拖拽排序和删除。"
      >
        <Upload
          multiple
          accept=".jpg,.jpeg,.png,.gif,.webp,.bmp"
          beforeUpload={() => false}
          fileList={imageFileList}
          onChange={onImagesChange}
          showUploadList={false}
          disabled={creating}
        >
          <Button icon={<CloudUploadOutlined />} disabled={creating}>选择图片</Button>
        </Upload>

        <div
          style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}
          onDragOver={(e) => {
            e.preventDefault();
            if (e.target === e.currentTarget && dragUid) {
              if (dropHint?.uid !== '__end__' || dropHint?.position !== 'end') {
                setDropHint({ uid: '__end__', position: 'end' });
              }
            }
          }}
          onDrop={(e) => {
            if (e.target !== e.currentTarget) return;
            e.preventDefault();
            const fromUid = readDragUid(e);
            handleDropToEnd(fromUid);
          }}
        >
          {(imageFileList || []).map((f, idx) => {
            const hint = dropHint?.uid === f.uid ? dropHint.position : null;
            const borderStyle = hint === 'before'
              ? { borderLeft: '3px solid #1677ff' }
              : hint === 'after'
                ? { borderRight: '3px solid #1677ff' }
                : {};

            return (
              <div
                key={f.uid}
                draggable
                onDragStart={(e) => {
                  try {
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('application/xhsnote-image-uid', f.uid);
                    e.dataTransfer.setData('text/plain', f.uid);
                  } catch (_) {
                    // ignore
                  }
                  setDragUid(f.uid);
                }}
                onDragEnd={() => {
                  setDragUid(null);
                  setDropHint(null);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  try {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const pos = x < rect.width / 2 ? 'before' : 'after';
                    if (dropHint?.uid !== f.uid || dropHint?.position !== pos) {
                      setDropHint({ uid: f.uid, position: pos });
                    }
                  } catch (_) {
                    // ignore
                  }
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const fromUid = readDragUid(e);
                  let pos = 'before';
                  try {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    pos = x < rect.width / 2 ? 'before' : 'after';
                  } catch (_) {
                    pos = dropHint?.uid === f.uid ? dropHint.position : 'before';
                  }
                  handleDropOn(f.uid, pos, fromUid);
                }}
                style={{
                  width: 120,
                  border: dragUid === f.uid ? '1px dashed #1677ff' : '1px solid #f0f0f0',
                  borderRadius: 8,
                  padding: 6,
                  background: '#fff',
                  cursor: 'move',
                  ...borderStyle,
                }}
              >
                <div style={{ position: 'relative' }}>
                  <img
                    src={getPreviewUrl(f)}
                    alt={f.name}
                    style={{ width: '100%', height: 90, objectFit: 'cover', borderRadius: 6 }}
                  />
                  <Button
                    danger
                    size="small"
                    type="primary"
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onRemoveImage(f.uid);
                    }}
                    style={{ position: 'absolute', top: 6, right: 6 }}
                    disabled={creating}
                  />
                </div>
                <div style={{ fontSize: 12, marginTop: 6, color: '#666' }}>
                  {idx + 1}/{(imageFileList || []).length}
                </div>
              </div>
            );
          })}
          <div
            key="__xhs_note_end_drop__"
            onDragOver={(e) => {
              e.preventDefault();
              if (dropHint?.uid !== '__end__' || dropHint?.position !== 'end') {
                setDropHint({ uid: '__end__', position: 'end' });
              }
            }}
            onDrop={(e) => {
              e.preventDefault();
              const fromUid = readDragUid(e);
              handleDropToEnd(fromUid);
            }}
            style={{
              flexBasis: '100%',
              height: 44,
              border: dropHint?.uid === '__end__' ? '2px dashed #1677ff' : '1px dashed #d9d9d9',
              borderRadius: 8,
              background: '#fafafa',
              color: '#666',
              fontSize: 12,
              display: (imageFileList || []).length > 0 ? (dragUid ? 'flex' : 'none') : 'none',
              alignItems: 'center',
              justifyContent: 'center',
              userSelect: 'none',
            }}
          >
            拖到这里放到最后
          </div>
        </div>
      </Form.Item>
    </Form>
  </Modal>
);

export default XhsNoteModal;
